import { AlertCircle } from "lucide-react";
import React, { useContext, useEffect, useMemo } from "react";
import { MultiSelect, Alert, Loader } from "@mantine/core";
import { AuthContext } from "../../providers/AuthProvider";
import useSegmentListApiService from "../../features/segments/services/api/SegmentListApiService";
import useSegmentAssignmentPreviewApiService from "../../features/odp-management/services/api/SegmentAssignmentPreviewApiService";

interface SegmentSelectorProps {
  value: string[];
  onChange: (value: string[]) => void;
  label?: string;
  description?: string;
  placeholder?: string;
  error?: string;
}

const SegmentSelector: React.FC<SegmentSelectorProps> = ({
  value,
  onChange,
  label = "Select employee segments",
  description = "Choose which employee segments should receive this",
  placeholder = "Select segments",
  error,
}) => {
  const auth = useContext(AuthContext);

  // Fetch all active segments. We intentionally do NOT request
  // include_match_count here: that computation scans the whole employee table
  // once per segment and times out for large employers, leaving the picker
  // empty. The accurate deduplicated count is fetched via the preview endpoint
  // below. Same fix as v1.82.1 for the EWA segmentation block drawer.
  const {
    data: segmentsData,
    isLoading: isLoadingSegments,
    isError: isSegmentsError,
  } = useSegmentListApiService(
    {
      employer: auth?.employer?.id ?? 0,
    },
    !!auth?.employer?.id
  );

  const availableSegments = useMemo(() => {
    if (!segmentsData?.results) {
      return [];
    }

    return segmentsData.results
      .filter((segment) => segment.is_active)
      .map((segment) => ({
        value: segment.id.toString(),
        label: segment.name,
      }));
  }, [segmentsData]);

  // Preview API to get deduplicated employee count
  const {
    mutate: fetchPreview,
    data: previewData,
    isLoading: isLoadingPreview,
  } = useSegmentAssignmentPreviewApiService();

  // Fetch deduplicated count when segments change
  useEffect(() => {
    if (value.length > 0) {
      const segmentIds = value.map((id) => parseInt(id, 10));
      fetchPreview({ segment_ids: segmentIds });
    }
  }, [value, fetchPreview]);

  const estimatedMatch = previewData?.total_matched_employee_count ?? 0;

  return (
    <div>
      <div className="font-medium text-[14px] text-[#062133] mb-2">{label}</div>
      {description && (
        <p className="text-[12px] text-[#839099] mb-2 leading-4">
          {description}
        </p>
      )}
      <MultiSelect
        placeholder={isSegmentsError ? "Couldn't load segments" : placeholder}
        data={availableSegments}
        value={value}
        onChange={onChange}
        disabled={isLoadingSegments || isSegmentsError}
        searchable
        clearable
        error={
          isSegmentsError
            ? "Couldn't load segments. Please refresh and try again."
            : error
        }
        nothingFound="No segments found"
        styles={{
          input: {
            border:
              error || isSegmentsError
                ? "1px solid #ED7687"
                : "1px solid #cdd3d6",
            borderRadius: "4px",
            minHeight: "36px",
          },
          value: {
            backgroundColor: "#384d5c",
            color: "white",
            fontWeight: 400,
            fontSize: "12px",
            borderRadius: "4px",
            padding: "4px 8px",
          },
          defaultValueRemove: {
            color: "white",
            borderLeftColor: "rgba(255, 255, 255, 0.3)",
          },
        }}
      />

      {/* Estimated match alert */}
      {value.length > 0 && (
        <Alert
          icon={
            isLoadingPreview ? (
              <Loader size={16} color="orange" />
            ) : (
              <AlertCircle size={16} />
            )
          }
          color="orange"
          className="mt-2"
          styles={{
            root: {
              backgroundColor: "#FFF4EA",
              borderRadius: "8px",
              border: "none",
            },
            message: {
              fontWeight: 500,
              fontSize: "12px",
              color: "#FF922B",
            },
            icon: {
              color: "#FF922B",
            },
          }}
        >
          {isLoadingPreview
            ? "Calculating..."
            : `Estimated match: ${estimatedMatch} Employees`}
        </Alert>
      )}
    </div>
  );
};

export default SegmentSelector;
